import React, {
  KeyboardEvent,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  ClockIcon,
  ExclamationIcon,
  MailIcon,
  ReplyIcon,
  TrashIcon,
  TruckIcon,
} from '@heroicons/react/outline';
import { Button } from 'components/Button';
import { withNotification } from 'components/Notification';
import { Toggle } from 'components/Toggle';
import { NonNegativeNumberInput } from 'components/NonNegativeNumberInput';
import { UnsavedChangesGuard } from 'components/UnsavedChangesGuard';
import { SAVE_OUTBOUND_SHIPMENT_ALERT_SETTINGS } from 'gql/mutations';
import { GET_OUTBOUND_SHIPMENT_ALERT_SETTINGS } from 'gql/queries';
import { useSettingsSaveState } from 'hooks/useSettingsSaveState';
import {
  DEFAULT_OUTBOUND_SHIPMENT_ALERT_FORM,
  normalizeOutboundShipmentAlertSettings,
  OutboundShipmentAlertFormState,
  OutboundShipmentAlertSettings,
  OutboundShipmentAlertTeamMember,
  OutboundShipmentAlertType,
} from 'types/shipment-alert';
import { apiErrorHandler, NotificationTypes, SizeEnum } from 'utils';

type OutboundShipmentAlertsProps = {
  storeId?: string;
  teamMembers?: OutboundShipmentAlertTeamMember[] | null;
  showNotification: any;
};

type PolicyConfig = {
  type: OutboundShipmentAlertType;
  title: string;
  description: string;
  icon: React.FC<any>;
};

const policyConfig: PolicyConfig[] = [
  {
    type: 'STUCK_IN_TRANSIT',
    title: 'Stuck in transit',
    description: 'No carrier movement for the selected number of days.',
    icon: ClockIcon,
  },
  {
    type: 'RETURNED_TO_SENDER',
    title: 'Returned to sender',
    description: 'The carrier is returning the shipment to your team.',
    icon: ReplyIcon,
  },
  {
    type: 'FAILED_DELIVERY_ATTEMPT',
    title: 'Failed delivery attempt',
    description: 'The carrier could not complete a delivery attempt.',
    icon: ExclamationIcon,
  },
];

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const defaultStuckThresholdDays =
  DEFAULT_OUTBOUND_SHIPMENT_ALERT_FORM.policies.find(
    (policy) => policy.type === 'STUCK_IN_TRANSIT',
  )?.thresholdDays ?? 7;

const isValidStuckThreshold = (thresholdDays: number | null | undefined) =>
  Number.isInteger(thresholdDays) &&
  (thresholdDays ?? 0) >= 1 &&
  (thresholdDays ?? 0) <= 90;

const cloneDefaultForm = (): OutboundShipmentAlertFormState => ({
  recipientUserIds: [],
  recipientEmails: [],
  policies: DEFAULT_OUTBOUND_SHIPMENT_ALERT_FORM.policies.map((policy) => ({
    ...policy,
  })),
});

export const OutboundShipmentAlerts = withNotification(
  ({ storeId, teamMembers, showNotification }: OutboundShipmentAlertsProps) => {
    const [form, setForm] =
      useState<OutboundShipmentAlertFormState>(cloneDefaultForm);
    const [emailInput, setEmailInput] = useState('');
    const [emailError, setEmailError] = useState('');
    const [formError, setFormError] = useState('');
    const activeStoreTokenRef = useRef<{ storeId?: string } | null>(null);
    const previousStoreIdRef = useRef(storeId);
    const availableTeamMembers = teamMembers ?? [];

    useLayoutEffect(() => {
      // A new token distinguishes separate visits to the same store.
      activeStoreTokenRef.current = { storeId };
      return () => {
        activeStoreTokenRef.current = null;
      };
    }, [storeId]);

    const { data, loading, error, refetch } = useQuery(
      GET_OUTBOUND_SHIPMENT_ALERT_SETTINGS,
      {
        variables: { input: { storeId } },
        skip: !storeId,
      },
    );
    const [saveSettings, { loading: saving }] = useMutation(
      SAVE_OUTBOUND_SHIPMENT_ALERT_SETTINGS,
    );

    const remoteSettings = data?.getOutboundShipmentAlertSettings as
      | OutboundShipmentAlertSettings
      | null
      | undefined;
    const settingsForStore =
      remoteSettings?.storeId === storeId ? remoteSettings : null;
    const resetKey = useMemo(
      () =>
        settingsForStore
          ? `${storeId}:${JSON.stringify(settingsForStore)}`
          : `${storeId || 'no-store'}:${loading ? 'loading' : 'default'}`,
      [loading, settingsForStore, storeId],
    );
    const {
      hasUnsavedChanges: hasUnsavedFormChanges,
      saveStatus,
      captureSaveSnapshot,
      markSaved,
      markSaveFailed,
    } = useSettingsSaveState(form, resetKey);
    const hasPendingEmail = emailInput.trim().length > 0;
    const hasUnsavedChanges = hasUnsavedFormChanges || hasPendingEmail;

    useEffect(() => {
      const storeChanged = previousStoreIdRef.current !== storeId;
      previousStoreIdRef.current = storeId;

      if (settingsForStore) {
        setForm(normalizeOutboundShipmentAlertSettings(settingsForStore));
      } else if (storeChanged || !loading) {
        setForm(cloneDefaultForm());
      } else {
        return;
      }

      setEmailInput('');
      setEmailError('');
      setFormError('');
    }, [loading, settingsForStore, storeId]);

    const selectedUserIds = useMemo(
      () => new Set(form.recipientUserIds),
      [form.recipientUserIds],
    );
    const enabledPolicyCount = form.policies.filter(
      (policy) => policy.enabled,
    ).length;

    const updatePolicy = (
      type: OutboundShipmentAlertType,
      values: Partial<{ enabled: boolean; thresholdDays: number | null }>,
    ) => {
      setForm((current) => ({
        ...current,
        policies: current.policies.map((policy) => {
          if (policy.type !== type) return policy;

          const updatedPolicy = { ...policy, ...values };
          if (
            type === 'STUCK_IN_TRANSIT' &&
            !updatedPolicy.enabled &&
            !isValidStuckThreshold(updatedPolicy.thresholdDays)
          ) {
            updatedPolicy.thresholdDays = defaultStuckThresholdDays;
          }

          return updatedPolicy;
        }),
      }));
      setFormError('');
    };

    const toggleTeamMember = (id: string) => {
      setForm((current) => ({
        ...current,
        recipientUserIds: current.recipientUserIds.includes(id)
          ? current.recipientUserIds.filter((userId) => userId !== id)
          : [...current.recipientUserIds, id],
      }));
      setFormError('');
    };

    const addEmail = () => {
      const normalizedEmail = emailInput.trim().toLowerCase();
      const teamEmails = availableTeamMembers
        .map((member) => member.email?.toLowerCase())
        .filter(Boolean);

      if (!emailPattern.test(normalizedEmail)) {
        setEmailError('Enter a valid email address.');
        return;
      }
      if (
        form.recipientEmails.some(
          (email) => email.toLowerCase() === normalizedEmail,
        ) ||
        teamEmails.includes(normalizedEmail)
      ) {
        setEmailError('This email is already available as a recipient.');
        return;
      }

      setForm((current) => ({
        ...current,
        recipientEmails: [...current.recipientEmails, normalizedEmail],
      }));
      setEmailInput('');
      setEmailError('');
      setFormError('');
    };

    const removeEmail = (email: string) => {
      setForm((current) => ({
        ...current,
        recipientEmails: current.recipientEmails.filter(
          (item) => item !== email,
        ),
      }));
      setFormError('');
    };

    const handleEmailKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      addEmail();
    };

    const handleSave = async (): Promise<boolean> => {
      if (!storeId) {
        setFormError('Choose a store before saving.');
        return false;
      }

      const stuckPolicy = form.policies.find(
        (policy) => policy.type === 'STUCK_IN_TRANSIT',
      );
      // The API requires a valid threshold for disabled policies as well.
      if (!isValidStuckThreshold(stuckPolicy?.thresholdDays)) {
        setFormError('Enter a whole number from 1 to 90.');
        return false;
      }
      if (hasPendingEmail) {
        setEmailError(
          emailPattern.test(emailInput.trim().toLowerCase())
            ? 'Add this email or clear the field before saving.'
            : 'Enter a valid email address.',
        );
        return false;
      }
      if (
        enabledPolicyCount > 0 &&
        form.recipientUserIds.length + form.recipientEmails.length === 0
      ) {
        setFormError('Choose at least one recipient for enabled alerts.');
        return false;
      }

      const submittedSnapshot = captureSaveSnapshot();
      const submittedStoreToken = activeStoreTokenRef.current;
      if (!submittedStoreToken) return false;

      try {
        await saveSettings({
          variables: {
            input: {
              storeId,
              recipientUserIds: form.recipientUserIds,
              recipientEmails: form.recipientEmails,
              policies: form.policies,
            },
          },
        });
        if (activeStoreTokenRef.current !== submittedStoreToken) return false;

        markSaved(submittedSnapshot);
        setFormError('');
        showNotification(
          NotificationTypes.success,
          'Shipping alert settings saved',
        );
        return true;
      } catch (saveError) {
        if (activeStoreTokenRef.current !== submittedStoreToken) return false;

        markSaveFailed();
        const parsedError = apiErrorHandler(saveError as any);
        showNotification(
          NotificationTypes.error,
          parsedError?.message || 'Shipping alert settings could not be saved',
        );
        return false;
      }
    };

    if (loading && !settingsForStore) {
      return (
        <section className="rounded-xl border border-[#d8dfeb] bg-white p-6">
          <div
            className="flex min-h-[180px] items-center justify-center text-sm text-[#64748b]"
            role="status"
          >
            Loading shipping alerts…
          </div>
        </section>
      );
    }

    if (error) {
      return (
        <section className="rounded-xl border border-red-200 bg-white p-6">
          <h2 className="text-lg font-semibold text-[#0b1c30]">
            Outbound shipment alerts
          </h2>
          <p className="mt-2 text-sm text-red-700">
            Shipping alert settings could not be loaded.
          </p>
          <Button className="mt-4" size={SizeEnum.sm} onClick={() => refetch()}>
            Try again
          </Button>
        </section>
      );
    }

    return (
      <section className="rounded-xl border border-[#d8dfeb] bg-white shadow-[0_4px_20px_rgba(15,23,42,0.05)]">
        <UnsavedChangesGuard
          hasUnsavedChanges={hasUnsavedChanges}
          onSave={handleSave}
        />

        <div className="flex flex-col gap-4 border-b border-[#e5ebf4] px-6 py-5 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-start gap-4">
            <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[#eef5ff] text-[#2563eb]">
              <TruckIcon className="h-5 w-5" aria-hidden="true" />
            </span>
            <div>
              <h2 className="text-lg font-semibold text-[#0b1c30]">
                Outbound shipment alerts
              </h2>
              <p className="mt-1 text-sm text-[#64748b]">
                Email your team when a shipment needs attention.
              </p>
              <p className="mt-1 text-xs font-medium text-[#737686]">
                Email · {enabledPolicyCount} of {policyConfig.length} active
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {hasUnsavedChanges ? (
              <span
                role="status"
                className="text-xs font-semibold text-amber-700"
              >
                Unsaved changes
              </span>
            ) : null}
            {saveStatus === 'saved' && !hasUnsavedChanges ? (
              <span
                role="status"
                className="text-xs font-semibold text-green-700"
              >
                Saved
              </span>
            ) : null}
            {saveStatus === 'error' ? (
              <span
                role="status"
                className="text-xs font-semibold text-red-700"
              >
                Not saved
              </span>
            ) : null}
            <Button
              size={SizeEnum.sm}
              onClick={handleSave}
              isLoading={saving}
              disabled={!storeId || !hasUnsavedChanges}
            >
              {saving ? 'Saving…' : 'Save shipping alerts'}
            </Button>
          </div>
        </div>

        <div className="grid gap-6 p-6 lg:grid-cols-2">
          <div>
            <h3 className="text-sm font-semibold text-[#0b1c30]">
              Alert types
            </h3>
            <div className="mt-3 space-y-3">
              {policyConfig.map((config) => {
                const policy =
                  form.policies.find((item) => item.type === config.type) ??
                  DEFAULT_OUTBOUND_SHIPMENT_ALERT_FORM.policies.find(
                    (item) => item.type === config.type,
                  );
                if (!policy) return null;

                const Icon = config.icon;

                return (
                  <div
                    key={config.type}
                    className="rounded-lg border border-[#e5ebf4] p-4"
                  >
                    <div className="flex items-start gap-3">
                      <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-[#eef5ff] text-[#2563eb]">
                        <Icon className="h-4 w-4" aria-hidden="true" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-[#0b1c30]">
                          {config.title}
                        </p>
                        <p className="mt-1 text-xs leading-5 text-[#64748b]">
                          {config.description}
                        </p>
                      </div>
                      <Toggle
                        isEnabled={policy.enabled}
                        onChange={(enabled) =>
                          updatePolicy(config.type, { enabled })
                        }
                        ariaLabel={config.title}
                        isSmall
                        disabled={!storeId}
                      />
                    </div>

                    {config.type === 'STUCK_IN_TRANSIT' ? (
                      <div className="mt-3 flex items-center gap-3 pl-11">
                        <label
                          htmlFor="shipment-alert-stuck-days"
                          className="text-xs font-medium text-[#5c647a]"
                        >
                          Alert after
                        </label>
                        <NonNegativeNumberInput
                          id="shipment-alert-stuck-days"
                          aria-label="Days without movement"
                          min={1}
                          max={90}
                          value={policy.thresholdDays ?? ''}
                          onChange={(event) => {
                            const rawValue = event.target.value;
                            updatePolicy(config.type, {
                              thresholdDays:
                                rawValue === '' ? null : Number(rawValue),
                            });
                          }}
                          disabled={!policy.enabled || !storeId}
                          className="w-20 rounded-md border border-[#cbd5e1] px-2.5 py-1.5 text-sm text-[#0b1c30] disabled:bg-[#f1f5f9] disabled:text-[#94a3b8]"
                        />
                        <span className="text-xs text-[#64748b]">
                          days without movement
                        </span>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-[#0b1c30]">Recipients</h3>
            <p className="mt-1 text-xs leading-5 text-[#64748b]">
              These recipients apply only to outbound shipment alerts.
            </p>

            <div className="mt-3 overflow-hidden rounded-lg border border-[#e5ebf4]">
              {availableTeamMembers.length ? (
                availableTeamMembers.map((member) => (
                  <label
                    key={member.id}
                    className="flex cursor-pointer items-center gap-3 border-b border-[#e5ebf4] px-3 py-3 last:border-b-0 hover:bg-[#f8fbff]"
                  >
                    <input
                      type="checkbox"
                      checked={selectedUserIds.has(member.id)}
                      onChange={() => toggleTeamMember(member.id)}
                      aria-label={`Send outbound shipment alerts to ${member.email}`}
                      className="h-4 w-4 rounded border-[#cbd5e1] text-[#2563eb] focus:ring-[#2563eb]"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-[#0b1c30]">
                        {member.displayName || member.email}
                      </span>
                      <span className="block truncate text-xs text-[#64748b]">
                        {member.email}
                      </span>
                    </span>
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-[#737686]">
                      {member.role}
                    </span>
                  </label>
                ))
              ) : (
                <p className="px-3 py-4 text-sm text-[#64748b]">
                  No store users are available.
                </p>
              )}
            </div>

            <div className="mt-4">
              <label
                htmlFor="shipment-alert-recipient-email"
                className="text-xs font-semibold text-[#0b1c30]"
              >
                Additional recipient email
              </label>
              <div className="mt-1.5 flex gap-2">
                <div className="relative min-w-0 flex-1">
                  <MailIcon
                    className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-[#94a3b8]"
                    aria-hidden="true"
                  />
                  <input
                    id="shipment-alert-recipient-email"
                    type="email"
                    value={emailInput}
                    onChange={(event) => {
                      setEmailInput(event.target.value);
                      setEmailError('');
                    }}
                    onKeyDown={handleEmailKeyDown}
                    placeholder="ops@company.com"
                    className="w-full rounded-md border border-[#cbd5e1] py-2 pl-9 pr-3 text-sm text-[#0b1c30] placeholder:text-[#94a3b8]"
                  />
                </div>
                <Button
                  size={SizeEnum.sm}
                  variant="outlined"
                  onClick={addEmail}
                >
                  Add email
                </Button>
              </div>
              {emailError ? (
                <p
                  role="alert"
                  className="mt-1.5 text-xs font-medium text-red-700"
                >
                  {emailError}
                </p>
              ) : null}
            </div>

            {form.recipientEmails.length ? (
              <ul className="mt-3 space-y-2" aria-label="Additional recipients">
                {form.recipientEmails.map((email) => (
                  <li
                    key={email}
                    className="flex items-center justify-between gap-3 rounded-md bg-[#f8fafc] px-3 py-2"
                  >
                    <span className="truncate text-sm text-[#334155]">
                      {email}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeEmail(email)}
                      aria-label={`Remove ${email}`}
                      className="rounded p-1 text-[#64748b] hover:bg-white hover:text-red-600"
                    >
                      <TrashIcon className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        </div>

        {formError ? (
          <p
            role="alert"
            className="border-t border-red-100 bg-red-50 px-6 py-3 text-sm font-medium text-red-700"
          >
            {formError}
          </p>
        ) : null}
      </section>
    );
  },
);
